import { AfterViewInit, Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UnifiedBallotStatus } from '../../../../../shared/enums/unified-ballot-status-enum';
import { BallotType } from '../../../../../shared/enums/ballot-type-enum';
import { IOption } from '../../../../controls/models/option';
import { of, Subscription, switchMap } from 'rxjs';
import { BallotSearchService } from '../../services/ballot-search.service';
import { IBallotTypeEnumValue, IConnectEnumValue } from '../../../../../shared/models/connect-enum';
import { ConnectEnumUtility } from '../../../../../shared/utilities/connect-enum-utility';
import { IApiResponse } from '../../../../../shared/models/api-response';

@Component({
    selector: 'app-select-multiple-ballot-status',
    templateUrl: './select-multiple-ballot-status.component.html',
    styleUrl: './select-multiple-ballot-status.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SelectMultipleBallotStatusComponent),
            multi: true
        }
    ],
    standalone: false
})

export class SelectMultipleBallotStatusComponent implements ControlValueAccessor, OnInit, OnDestroy, OnChanges, AfterViewInit {

  @Input() id: string = 'select-ballot-status';
  @Input() label: string = 'Filter by Status';
  @Input() selectedBallotTypes: BallotType[] = [];
  @Output() viewLoaded = new EventEmitter<void>();

  onChange: any = () => {};
  onTouched: any = () => {};
  isDisabled: boolean = false;

  private _values: UnifiedBallotStatus[] = [];
  get values() {
    return this._values;
  }
  set values(newValue: UnifiedBallotStatus[]) {
    this._values = newValue;
    this.onChange(newValue);
  }

  options: IOption<UnifiedBallotStatus>[] = [];

  subscriptions: Subscription = new Subscription();
  viewableBallotStatusEnums: IConnectEnumValue<UnifiedBallotStatus>[] = [];
  ballotTypeEnums: IBallotTypeEnumValue[] = [];

  constructor(private _ballotSearchService: BallotSearchService) { }

  ngOnInit(): void {
    this.getViewableBallotStatusForUser();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes['selectedBallotTypes']) {
      this.setSelectOptions();
    }
  }

  ngAfterViewInit(): void {
    this.viewLoaded.emit();
  }

  writeValue(obj: any): void {
    this._values = obj ? obj : [];
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }


  getViewableBallotStatusForUser(): void {
    const subscription =
      this._ballotSearchService.getBallotTypeEnums().pipe(
        switchMap((ballotTypeResponse) => {

          if (ballotTypeResponse && ballotTypeResponse.success) {
            this.ballotTypeEnums = ballotTypeResponse.content;
            return this._ballotSearchService.getViewableBallotStatusEnums();
          }
          else {
            console.error('Error getting ballot types', ballotTypeResponse.message);
            return of<IApiResponse<IConnectEnumValue<UnifiedBallotStatus>[]> | null>(null);
          }
        }
      ))
      .subscribe({
        next: (response) => {
          if(response && response.success) {
            this.viewableBallotStatusEnums = response.content;
            this.setSelectOptions();
          } else {
            console.error('Error getting viewable ballot status', response?.message);
          }
        },
        error: (error) => {
          console.error(error);
        }
      });

      this.subscriptions.add(subscription);
  }

  setSelectOptions(): void {
    if(this.selectedBallotTypes === undefined
        || this.selectedBallotTypes.length === 0
        || this.viewableBallotStatusEnums.length === 0) {
      this.options = this.viewableBallotStatusEnums.map((statusEnum: IConnectEnumValue<UnifiedBallotStatus>) => {
        return {
          value: statusEnum.id,
          label: statusEnum.displayText
        };
      });
    } else {
       const ballotStatusForSelectedBallotTypes = this.selectedBallotTypes
        .flatMap(t => ConnectEnumUtility.getUnifiedBallotStatusMapping(this.ballotTypeEnums, t))
        .filter(statusId => statusId !== undefined)

      const uniqueBallotStatus = [...new Set(ballotStatusForSelectedBallotTypes)];

      const newOptions = this.viewableBallotStatusEnums
        .filter(x => uniqueBallotStatus.includes(x.id))
        .map((statusEnum: IConnectEnumValue<UnifiedBallotStatus>) => {
          return {
            value: statusEnum.id,
            label: statusEnum.displayText
          };
        });

      this.options = newOptions;

      // Unselect any values that are not in the new list of options
      const currentValues = [...this.values];
      this.values = currentValues.filter(x => this.options.map(y => y.value).includes(x));
    }
  }
}
